import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "wouter";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Printer, ArrowLeft, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import type { OrderWithItems, DeliveryAdjustment } from "@shared/schema";
import { useCompanyLogo } from "@/hooks/use-company-logo";

interface AdjustmentItem {
  orderItemId: string;
  productName: string;
  orderedQty: number;
  deliveredQty: number;
  reason: string;
}

export default function ProductionDeliveryNotePage() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const logoUrl = useCompanyLogo();

  const [showAdjustmentForm, setShowAdjustmentForm] = useState(false);
  const [allFailed, setAllFailed] = useState(false);
  const [adjustmentItems, setAdjustmentItems] = useState<AdjustmentItem[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const { data: order, isLoading } = useQuery<OrderWithItems>({
    queryKey: ["/api/production/orders", id],
  });

  const { data: existingAdjustments } = useQuery<DeliveryAdjustment[]>({
    queryKey: ["/api/production/orders", id, "adjustments"],
    queryFn: () => fetch(`/api/production/orders/${id}/adjustments`).then(r => r.ok ? r.json() : []),
    enabled: !!id,
  });

  const { data: settings } = useQuery<Record<string, string>>({
    queryKey: ["/api/settings"],
  });

  const submitMutation = useMutation({
    mutationFn: async (items: AdjustmentItem[]) => {
      // Only send fields the server accepts; orderedQty and productName are derived server-side
      const payload = items.map(i => ({
        orderItemId: i.orderItemId,
        deliveredQty: i.deliveredQty,
        reason: i.reason || undefined,
      }));
      const res = await fetch(`/api/production/orders/${id}/adjustments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: payload }),
      });
      if (!res.ok) throw new Error("Serverfel");
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Avvikelse registrerad", description: "En admin kommer att granska avvikelsen." });
      setShowAdjustmentForm(false);
      setSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ["/api/production/orders", id, "adjustments"] });
    },
    onError: () => toast({ title: "Fel", description: "Kunde inte registrera avvikelse", variant: "destructive" }),
  });

  const getUnitLabel = (unit: string) => {
    switch (unit) {
      case "pcs": return "st";
      case "bag": return "påse";
      case "box": return "låda";
      default: return unit;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const initAdjustmentForm = () => {
    if (!order?.items) return;
    setAdjustmentItems(order.items.map(item => ({
      orderItemId: item.id,
      productName: item.product?.name || "",
      orderedQty: item.quantity,
      deliveredQty: item.quantity,
      reason: "",
    })));
    setAllFailed(false);
    setShowAdjustmentForm(true);
  };

  const handleAllFailed = (checked: boolean) => {
    setAllFailed(checked);
    if (checked) {
      setAdjustmentItems(prev => prev.map(i => ({ ...i, deliveredQty: 0 })));
    }
  };

  const updateItem = (idx: number, field: keyof AdjustmentItem, value: string | number) => {
    setAdjustmentItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleSubmit = () => {
    const changed = adjustmentItems.filter(i => i.deliveredQty !== i.orderedQty || i.reason);
    if (changed.length === 0) {
      toast({ title: "Inga avvikelser", description: "Inga mängder har ändrats.", variant: "destructive" });
      return;
    }
    submitMutation.mutate(changed);
  };

  const statusBadge = (status: string) => {
    if (status === "approved") return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Godkänd</Badge>;
    if (status === "rejected") return <Badge variant="secondary">Avvisad</Badge>;
    return <Badge variant="outline">Väntande</Badge>;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6 p-4">
        <h1 className="text-2xl font-bold">Order hittades inte</h1>
        <Button asChild variant="outline">
          <Link href="/production/pakare">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka
          </Link>
        </Button>
      </div>
    );
  }

  const hasPendingAdjustments = existingAdjustments && existingAdjustments.some(a => a.status === "pending");
  const hasAnyAdjustments = existingAdjustments && existingAdjustments.length > 0;

  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center justify-between gap-4 flex-wrap no-print">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link href="/production/pakare">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Leveranssedel</h1>
            <p className="text-muted-foreground">
              Order #{order.orderNumber}
            </p>
          </div>
        </div>
        <Button onClick={handlePrint} data-testid="button-print">
          <Printer className="h-4 w-4 mr-2" />
          Skriv ut
        </Button>
      </div>

      <div className="delivery-note-container a4-page mx-auto bg-background dark:bg-white text-foreground dark:text-black print:bg-white print:text-black shadow-lg print:shadow-none">
        <div className="p-8 space-y-6">
          <div className="flex justify-between items-start">
            <h1 className="text-4xl font-bold italic">Följesedel</h1>
            <img
              src={logoUrl}
              alt="Rätt Order"
              className="h-16 w-auto max-w-[120px] object-contain"
              data-testid="img-delivery-logo"
            />
          </div>

          <div className="h-1 bg-primary w-full" />

          <div className="grid grid-cols-2 gap-8 pt-4">
            <div>
              {settings?.companyName && (
                <h2 className="font-bold text-lg mb-2">{settings.companyName}</h2>
              )}
              {settings?.companyAddress && (
                <p className="text-muted-foreground dark:text-gray-600 italic">{settings.companyAddress}</p>
              )}
              {settings?.companyPhone && (
                <p className="text-muted-foreground dark:text-gray-600 italic">Telefon: {settings.companyPhone}</p>
              )}
              {settings?.companyEmail && (
                <p className="text-muted-foreground dark:text-gray-600 italic">{settings.companyEmail}</p>
              )}
            </div>
            <div>
              <h2 className="font-bold text-lg mb-2">Mottagare</h2>
              <p className="text-muted-foreground dark:text-gray-600 italic">{order.client?.name}</p>
              {order.client?.address && (
                <p className="text-muted-foreground dark:text-gray-600 italic">{order.client.address}</p>
              )}
              {order.client?.phone && (
                <p className="text-muted-foreground dark:text-gray-600 italic">Tel: {order.client.phone}</p>
              )}
              {order.client?.email && (
                <p className="text-muted-foreground dark:text-gray-600 italic">{order.client.email}</p>
              )}
            </div>
          </div>

          <div className="pt-8">
            <div className="h-px bg-primary w-full mb-4" />
            <table className="w-full">
              <thead>
                <tr className="text-left">
                  <th className="py-2 font-semibold">Beskrivning</th>
                  <th className="py-2 font-semibold">Datum</th>
                  <th className="py-2 font-semibold text-right">Antal</th>
                  <th className="py-2 font-semibold text-right">Enhet</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map((item) => (
                  <tr key={item.id} className="border-t border-border dark:border-gray-200">
                    <td className="py-3">{item.product?.name}</td>
                    <td className="py-3 text-muted-foreground dark:text-gray-600">
                      {format(new Date(order.orderDate!), "yyyy-MM-dd", { locale: sv })}
                    </td>
                    <td className="py-3 text-right">{item.quantity}</td>
                    <td className="py-3 text-right">{getUnitLabel(item.product?.sellUnit || "pcs")}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border dark:border-gray-300 font-semibold">
                  <td className="py-3">Totalt</td>
                  <td></td>
                  <td className="py-3 text-right">{order.items?.reduce((sum, item) => sum + item.quantity, 0) || 0}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {(order.client?.hasRut && order.client.rutDetails) || order.client?.notes ? (
            <div className="pt-4 grid grid-cols-2 gap-8">
              <div>
                {order.client?.hasRut && order.client.rutDetails && (
                  <>
                    <h3 className="font-semibold mb-2">Leveransrutt</h3>
                    <p className="text-muted-foreground dark:text-gray-600 italic">{order.client.rutDetails}</p>
                  </>
                )}
              </div>
              <div>
                {order.client?.notes && (
                  <>
                    <h3 className="font-semibold mb-2">Anteckningar</h3>
                    <p className="text-muted-foreground dark:text-gray-600 italic">{order.client.notes}</p>
                  </>
                )}
              </div>
            </div>
          ) : null}

        </div>
      </div>

      {/* Adjustment section — no-print */}
      <div className="no-print space-y-4 mx-auto" style={{ maxWidth: "210mm" }}>
        {/* Show existing adjustments */}
        {hasAnyAdjustments && (
          <div className="rounded-lg border p-4 space-y-3">
            <h2 className="font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Pågående avvikelse
            </h2>
            <div className="divide-y text-sm">
              {existingAdjustments!.map(adj => (
                <div key={adj.id} className="py-2 flex items-center justify-between gap-4">
                  <div>
                    <span className="font-medium">{adj.productName}</span>
                    <span className="text-muted-foreground ml-2">
                      Beställt: {adj.orderedQty} → Levererat: {adj.deliveredQty}
                    </span>
                    {adj.reason && <span className="text-muted-foreground ml-2">({adj.reason})</span>}
                  </div>
                  {statusBadge(adj.status)}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Adjustment form toggle */}
        {!hasPendingAdjustments && !submitted && (
          <div>
            <Button
              variant="outline"
              className="gap-2 border-amber-400 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950"
              onClick={() => showAdjustmentForm ? setShowAdjustmentForm(false) : initAdjustmentForm()}
              data-testid="button-toggle-adjustment"
            >
              <AlertTriangle className="h-4 w-4" />
              Registrera avvikelse
              {showAdjustmentForm ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>

            {showAdjustmentForm && (
              <div className="mt-4 rounded-lg border p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="all-failed"
                    checked={allFailed}
                    onChange={e => handleAllFailed(e.target.checked)}
                    className="h-4 w-4"
                    data-testid="checkbox-all-failed"
                  />
                  <label htmlFor="all-failed" className="text-sm font-medium cursor-pointer">
                    Hela ordern ej levererad (Leveransfel)
                  </label>
                </div>

                <div className="space-y-3">
                  {adjustmentItems.map((item, idx) => (
                    <div key={item.orderItemId} className="grid grid-cols-1 gap-2 p-3 bg-muted/40 rounded-md">
                      <div className="font-medium text-sm">{item.productName}</div>
                      <div className="flex flex-wrap gap-3 items-center">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">Beställt:</span>
                          <span className="font-medium">{item.orderedQty}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <label htmlFor={`delivered-${idx}`} className="text-muted-foreground">Levererat:</label>
                          <input
                            id={`delivered-${idx}`}
                            type="number"
                            min={0}
                            max={item.orderedQty}
                            value={item.deliveredQty}
                            onChange={e => updateItem(idx, "deliveredQty", Math.max(0, parseInt(e.target.value) || 0))}
                            disabled={allFailed}
                            className="w-20 px-2 py-1 border rounded text-sm bg-background disabled:opacity-50"
                            data-testid={`input-delivered-${idx}`}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          placeholder="Anledning (valfritt)"
                          value={item.reason}
                          onChange={e => updateItem(idx, "reason", e.target.value)}
                          className="flex-1 px-2 py-1 border rounded text-sm bg-background"
                          data-testid={`input-reason-${idx}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={handleSubmit}
                    disabled={submitMutation.isPending}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="button-submit-adjustment"
                  >
                    {submitMutation.isPending ? "Skickar..." : "Skicka avvikelse"}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => setShowAdjustmentForm(false)}
                    data-testid="button-cancel-adjustment"
                  >
                    Avbryt
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {submitted && (
          <div className="rounded-lg border border-green-300 bg-green-50 dark:bg-green-950 dark:border-green-800 p-4 text-green-800 dark:text-green-200 text-sm">
            Avvikelse registrerad. En admin kommer att granska den.
          </div>
        )}
      </div>

      <style>{`
        .a4-page {
          width: 210mm;
          min-height: 297mm;
          box-sizing: border-box;
        }
        
        @media screen and (max-width: 850px) {
          .a4-page {
            width: 100%;
            min-height: auto;
          }
        }
        
        @media print {
          @page {
            size: A4;
            margin: 0;
          }
          
          html, body {
            height: auto !important;
            overflow: visible !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          body * {
            visibility: hidden;
          }
          .delivery-note-container,
          .delivery-note-container * {
            visibility: visible;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .delivery-note-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            margin: 0;
            padding: 10mm;
            box-sizing: border-box;
            background: white !important;
            color: black !important;
            overflow: visible !important;
            page-break-after: avoid;
            page-break-inside: avoid;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
